<template>
    <div>
        <!-- Floating button -->
        <a v-if="!panelOpen" @click="openPanel" class="support-float" title="Bantuan / Tiket Support">
            <i class="fas fa-headset support-float-icon"></i>
            <span v-if="unreadCount > 0" class="support-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
        </a>

        <!-- Slide panel -->
        <transition name="slide-left">
            <div v-if="panelOpen" class="support-panel">
                <!-- Header -->
                <div class="support-header">
                    <div class="d-flex align-items-center">
                        <i class="fas fa-headset" style="font-size:20px; margin-right:10px;"></i>
                        <div>
                            <div style="font-weight:600; font-size:15px;">Support Center</div>
                            <div style="font-size:11px; opacity:0.85;">{{ workingHours.info }}</div>
                        </div>
                    </div>
                    <button @click="closePanel" class="support-close-btn"><i class="fas fa-times"></i></button>
                </div>

                <!-- Working hours banner -->
                <div :class="['support-status-banner', workingHours.is_open ? 'open' : 'closed']">
                    <span v-if="workingHours.is_open">
                        <i class="fas fa-circle" style="color:#10b981; font-size:8px;"></i>
                        Tim Support Online — siap membantu
                    </span>
                    <span v-else>
                        <i class="fas fa-moon" style="color:#9ca3af;"></i>
                        Di luar jam kerja. Tiket Anda akan dibalas pada jam berikutnya.
                    </span>
                </div>

                <!-- Tab nav -->
                <div class="support-tab-nav">
                    <button :class="['support-tab', { active: activeTab === 'list' }]" @click="activeTab = 'list'">
                        <i class="fas fa-list"></i> Tiket Saya
                        <span v-if="unreadCount > 0" class="support-tab-dot"></span>
                    </button>
                    <button :class="['support-tab', { active: activeTab === 'new' }]" @click="activeTab = 'new'">
                        <i class="fas fa-plus"></i> Buat Baru
                    </button>
                </div>

                <!-- List tab -->
                <div v-if="activeTab === 'list'" class="support-content">
                    <div v-if="loadingList" class="text-center p-3">
                        <div class="spinner-border spinner-border-sm"></div>
                    </div>
                    <div v-else-if="tickets.length === 0" class="text-center p-4 text-muted">
                        <i class="fas fa-inbox fa-2x mb-2" style="color:#d1d5db;"></i>
                        <div>Belum ada tiket</div>
                        <button @click="activeTab = 'new'" class="btn btn-sm btn-primary mt-2">Buat Tiket Pertama</button>
                    </div>
                    <div v-else>
                        <div v-for="t in tickets" :key="t.id" @click="openTicket(t.id)" class="support-ticket-item">
                            <div class="d-flex justify-content-between align-items-start">
                                <div style="flex:1; min-width:0;">
                                    <div class="support-ticket-subject">
                                        {{ t.subject }}
                                        <span v-if="t.user_unread_count > 0" class="support-unread-dot"></span>
                                    </div>
                                    <div class="support-ticket-meta">
                                        <span class="support-status" :class="'status-' + t.status">{{ statusLabel(t.status) }}</span>
                                        <span style="margin-left:8px;">{{ t.ticket_number }}</span>
                                    </div>
                                </div>
                                <small class="text-muted" style="white-space:nowrap;">{{ formatRelative(t.updated_at) }}</small>
                            </div>
                        </div>
                        <div class="text-center p-2">
                            <router-link to="/support" @click.native="closePanel" class="btn btn-sm btn-light">
                                Lihat Semua →
                            </router-link>
                        </div>
                    </div>
                </div>

                <!-- New ticket form -->
                <div v-if="activeTab === 'new'" class="support-content">
                    <div class="form-group">
                        <label class="small">Subject *</label>
                        <input v-model="newTicket.subject" class="form-control form-control-sm" placeholder="Ringkasan singkat masalah" maxlength="255">
                    </div>
                    <div class="form-group">
                        <label class="small">Kategori</label>
                        <select v-model="newTicket.category" class="form-control form-control-sm">
                            <option value="general">Umum</option>
                            <option value="bug">Bug / Error</option>
                            <option value="billing">Pembayaran</option>
                            <option value="feature">Saran Fitur</option>
                            <option value="other">Lainnya</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label class="small">Priority</label>
                        <select v-model="newTicket.priority" class="form-control form-control-sm">
                            <option value="low">Rendah</option>
                            <option value="normal">Normal</option>
                            <option value="high">Tinggi</option>
                            <option value="urgent">Urgent</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label class="small">Pesan *</label>
                        <textarea v-model="newTicket.message" class="form-control form-control-sm" rows="5" placeholder="Jelaskan masalah Anda secara detail..."></textarea>
                    </div>
                    <div class="form-group">
                        <label class="small">Lampiran (max 5 file, 25MB/file)</label>
                        <input type="file" multiple @change="onFileSelect" class="form-control form-control-sm" accept="*/*">
                        <div v-if="newTicket.files.length > 0" class="mt-2">
                            <div v-for="(f, i) in newTicket.files" :key="i" class="d-flex justify-content-between align-items-center small p-1" style="background:#f9fafb; border-radius:4px; margin-bottom:4px;">
                                <span><i class="fas fa-paperclip"></i> {{ f.name }} <span class="text-muted">({{ formatSize(f.size) }})</span></span>
                                <button @click="removeFile(i)" class="btn btn-sm btn-link text-danger p-0"><i class="fas fa-times"></i></button>
                            </div>
                        </div>
                    </div>
                    <button @click="submitTicket" :disabled="submitting || !canSubmit" class="btn btn-primary btn-block">
                        <span v-if="submitting"><i class="fas fa-spinner fa-spin"></i> Mengirim...</span>
                        <span v-else><i class="fas fa-paper-plane"></i> Kirim Tiket</span>
                    </button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import moment from 'moment';
export default {
    name: 'SupportWidget',
    data() {
        return {
            panelOpen: false,
            activeTab: 'list',
            loadingList: false,
            submitting: false,
            tickets: [],
            unreadCount: 0,
            workingHours: { is_open: true, info: 'Senin–Jumat, 10:00–20:00 WIB', next_open_at: 0 },
            pollInterval: null,
            newTicket: {
                subject: '',
                category: 'general',
                priority: 'normal',
                message: '',
                files: [],
            },
        };
    },
    computed: {
        canSubmit() {
            return this.newTicket.subject.trim().length >= 5
                && this.newTicket.message.trim().length >= 5;
        },
    },
    mounted() {
        this.loadUnreadCount();
        this.loadWorkingHours();
        // Poll unread count tiap 30 detik
        this.pollInterval = setInterval(() => this.loadUnreadCount(), 30000);
        // Listen WS event support_ticket_reply
        this.$root.$on('ws', this.handleWS);
    },
    beforeDestroy() {
        if (this.pollInterval) clearInterval(this.pollInterval);
        this.$root.$off('ws', this.handleWS);
    },
    methods: {
        handleWS(data) {
            if (data && data.type === 'support_ticket_reply') {
                this.loadUnreadCount();
            }
        },
        async loadUnreadCount() {
            try {
                const r = await this.$store.dispatch('support_ticket_unread_count');
                this.unreadCount = r.data.data.unread_count || 0;
            } catch (e) { /* silent */ }
        },
        async loadWorkingHours() {
            try {
                const r = await this.$store.dispatch('support_ticket_working_hours');
                this.workingHours = r.data.data;
            } catch (e) { /* silent */ }
        },
        async loadTickets() {
            this.loadingList = true;
            try {
                const r = await this.$store.dispatch('support_ticket_list', { limit: 10 });
                this.tickets = r.data.data.tickets || [];
            } catch (e) { /* silent */ }
            this.loadingList = false;
        },
        openPanel() {
            this.panelOpen = true;
            this.loadTickets();
            this.loadWorkingHours();
        },
        closePanel() {
            this.panelOpen = false;
        },
        openTicket(id) {
            this.closePanel();
            this.$router.push('/support/ticket/' + id);
        },
        onFileSelect(e) {
            const files = Array.from(e.target.files || []);
            for (const f of files) {
                if (f.size > 25 * 1024 * 1024) {
                    this.$toast.error(`File "${f.name}" melebihi 25MB`);
                    continue;
                }
                if (this.newTicket.files.length >= 5) {
                    this.$toast.error('Maksimal 5 file per pesan');
                    break;
                }
                this.newTicket.files.push(f);
            }
            e.target.value = '';
        },
        removeFile(i) {
            this.newTicket.files.splice(i, 1);
        },
        async submitTicket() {
            if (!this.canSubmit) return;
            this.submitting = true;
            try {
                const fd = new FormData();
                fd.append('subject', this.newTicket.subject.trim());
                fd.append('message', this.newTicket.message.trim());
                fd.append('category', this.newTicket.category);
                fd.append('priority', this.newTicket.priority);
                this.newTicket.files.forEach(f => fd.append('files', f));
                const r = await this.$store.dispatch('support_ticket_create', fd);
                this.$toast.success('Tiket berhasil dibuat: ' + r.data.data.ticket.ticket_number);
                this.newTicket = { subject: '', category: 'general', priority: 'normal', message: '', files: [] };
                this.activeTab = 'list';
                this.loadTickets();
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Gagal kirim tiket');
            }
            this.submitting = false;
        },
        statusLabel(s) {
            const map = { open: 'Open', in_progress: 'Diproses', waiting_user: 'Menunggu Anda', closed: 'Selesai' };
            return map[s] || s;
        },
        formatRelative(ts) {
            if (!ts) return '';
            return moment(ts).fromNow();
        },
        formatSize(n) {
            if (n < 1024) return n + ' B';
            if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
            return (n / (1024 * 1024)).toFixed(1) + ' MB';
        },
    },
};
</script>

<style scoped>
.support-float {
    cursor: pointer;
    position: fixed;
    width: 56px;
    height: 56px;
    bottom: 30px;
    right: 30px;
    background: linear-gradient(135deg, #1976d2 0%, #0d47a1 100%);
    color: #fff;
    border-radius: 50%;
    text-align: center;
    box-shadow: 0 4px 16px rgba(25, 118, 210, 0.4);
    z-index: 9998;
    transition: transform 0.2s, box-shadow 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
}
.support-float:hover {
    transform: scale(1.08);
    box-shadow: 0 6px 20px rgba(25, 118, 210, 0.6);
    color: #fff;
}
.support-float-icon {
    font-size: 24px;
    line-height: 1;
    color: #fff;
}
.support-badge {
    position: absolute;
    top: -4px;
    right: -4px;
    background: #ef4444;
    color: #fff;
    border-radius: 12px;
    padding: 2px 7px;
    font-size: 11px;
    font-weight: 600;
    min-width: 20px;
    line-height: 1.4;
    box-shadow: 0 2px 6px rgba(239, 68, 68, 0.4);
}
.support-panel {
    position: fixed;
    bottom: 20px;
    right: 20px;
    width: 360px;
    max-width: calc(100vw - 40px);
    height: 560px;
    max-height: calc(100vh - 40px);
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.18);
    z-index: 9999;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.support-header {
    background: linear-gradient(135deg, #1976d2 0%, #0d47a1 100%);
    color: #fff;
    padding: 14px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.support-close-btn {
    background: rgba(255, 255, 255, 0.15);
    border: none;
    color: #fff;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 13px;
}
.support-close-btn:hover { background: rgba(255, 255, 255, 0.3); }
.support-status-banner {
    padding: 10px 16px;
    font-size: 12px;
    font-weight: 500;
}
.support-status-banner.open { background: #ecfdf5; color: #047857; }
.support-status-banner.closed { background: #f3f4f6; color: #6b7280; }
.support-tab-nav {
    display: flex;
    border-bottom: 1px solid #e5e7eb;
}
.support-tab {
    flex: 1;
    padding: 12px 8px;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    color: #6b7280;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    position: relative;
}
.support-tab.active { color: #1976d2; border-bottom-color: #1976d2; }
.support-tab-dot {
    width: 6px;
    height: 6px;
    background: #ef4444;
    border-radius: 50%;
    display: inline-block;
    margin-left: 4px;
}
.support-content {
    flex: 1;
    overflow-y: auto;
    padding: 12px;
}
.support-ticket-item {
    padding: 10px 12px;
    border-radius: 8px;
    cursor: pointer;
    margin-bottom: 6px;
    transition: background 0.15s;
}
.support-ticket-item:hover { background: #f9fafb; }
.support-ticket-subject {
    font-weight: 500;
    font-size: 13px;
    color: #1f2937;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.support-unread-dot {
    width: 8px;
    height: 8px;
    background: #ef4444;
    border-radius: 50%;
    display: inline-block;
    margin-left: 6px;
}
.support-ticket-meta {
    font-size: 11px;
    color: #9ca3af;
    margin-top: 3px;
}
.support-status {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
}
.status-open { background: #fef3c7; color: #92400e; }
.status-in_progress { background: #dbeafe; color: #1e40af; }
.status-waiting_user { background: #fce7f3; color: #9d174d; }
.status-closed { background: #d1fae5; color: #065f46; }

.slide-left-enter-active, .slide-left-leave-active { transition: all 0.3s; }
.slide-left-enter, .slide-left-leave-to {
    transform: translateX(20px);
    opacity: 0;
}

@media (max-width: 480px) {
    .support-panel {
        bottom: 0;
        right: 0;
        width: 100vw;
        height: 100vh;
        max-height: 100vh;
        border-radius: 0;
    }
    .support-float { bottom: 20px; right: 20px; }
}
</style>
